import { defineStore } from "pinia";
import { markRaw, Raw } from "vue";
import { sdk, idToChannelName } from "@/js/sdk";
import { SdkError, ERROR_CODES, type BuildStatus as SdkBuildStatus } from "@signaloid/cloud-compute-engine-client";
import { isBuildActive, isBuildTerminated, Build, BuildStatus, type BinaryDownloadSurface } from "@/types/api/builds";
import { useEventBus, UseEventBusReturn } from "@vueuse/core";
import * as Sentry from "@sentry/vue";
import axios from "axios";
import posthog from "posthog-js";
import { CoreClassE } from "@/types/api/cores";

import type { EventBusKey } from "@vueuse/core";
import { RemoteAccessState, BuildBusEvent, BuildEventE } from "@/types/general";
import { useUserStore } from "./user";

import * as util from "@/js/utilities";
import { UserLimitsE } from "@/js/tierconfig";
import { TierLimitEventTypeE } from "@/eventBus/tierLimitEventBus";
import { useRootStore } from "@/stores/root";
import { useRealtimeStore } from "@/stores/realtime";
import { monitoringCaptureError } from "@/plugins/monitoring";
import { isRealtimeGiveUpError } from "@/js/sdkError";
import { TraceVariableWithTraceType } from "@/types/api/nodes";
import { LIST_CACHE_MAX_ENTRIES, hydrateCachedList, mirrorStoreListToCache } from "@/js/listCache";

const kC0MicroSDApplicationLimitBytes = 131072;
const kBinaryNotFoundSdkMessage = "The build binary was not found.";
const kBinaryMissingNotice =
	"We could not find the binary for this build. Build the repository again to create a new one.";

function binaryTooLargeNotice(sizeInBytes: number): string {
	return `This binary is ${sizeInBytes.toLocaleString("en-US")} bytes, more than the ${kC0MicroSDApplicationLimitBytes.toLocaleString("en-US")} bytes (128 KB) a C0-microSD holds. It was still downloaded.`;
}

// SDK exposes `SubscriptionClient` only via the return type of `channel.subscribe()`.
type RealtimeSubscription = Awaited<ReturnType<ReturnType<typeof sdk.realtime.channel>["subscribe"]>>;

// Realtime frames deliver the event as a JSON string; id/status sit under its `data`.
type BuildStatusPayload = { buildId?: string; BuildID?: string; status?: string; Status?: string };
type BuildStatusEnvelope = BuildStatusPayload & { data?: BuildStatusPayload };
type BuildStatusMessage = BuildStatusPayload & {
	data?: BuildStatusPayload;
	event?: string | BuildStatusEnvelope;
};

type StoreState = {
	buildHistory: undefined | Build[];
	activeBuilds: undefined | Build[];
	buildHistoryUpdateStatus: RemoteAccessState;
	activeBuildUpdateStatus: RemoteAccessState;
	buildEventBus: UseEventBusReturn<BuildBusEvent, any>;
	buildListContinuationKey: undefined | string;
	activeBuildsListContinuationKey: undefined | string;
	realtimeSubscription: undefined | RealtimeSubscription;
	realtimeConnectInFlight: Promise<boolean> | undefined;
	realtimeTeardownRequested: boolean;
	buildHistoryServedFromCache: boolean;
	buildIDsFetchedOverCache: Raw<Set<string>>;
	buildHistoryIsComplete: boolean;
	buildHistoryInitialisationInFlight: undefined | Promise<void>;
};

const BUILD_HISTORY_CACHE_KEY = "build-history";

// Cached lists are untrusted local input: keep only entries the app can actually render.
function isCachedBuild(entry: unknown): entry is Build {
	if (typeof entry !== "object" || entry === null) {
		return false;
	}
	const candidate = entry as Partial<Build>;
	return (
		typeof candidate.BuildID === "string" &&
		typeof candidate.Owner === "string" &&
		typeof candidate.Status === "string" &&
		typeof candidate.CreatedAt === "number" &&
		typeof candidate.UpdatedAt === "number" &&
		typeof candidate.Application === "object" &&
		candidate.Application !== null &&
		typeof candidate.Application.Type === "string" &&
		typeof candidate.BuildCoreSpecs === "object" &&
		candidate.BuildCoreSpecs !== null
	);
}

function extractBuildStatusUpdate(msg: BuildStatusMessage): { buildId?: string; status?: string } {
	let event: BuildStatusEnvelope | undefined;
	if (typeof msg?.event === "string") {
		try {
			event = JSON.parse(msg.event);
		} catch {
			event = undefined;
		}
	} else {
		event = msg?.event;
	}
	const payload = event?.data ?? msg?.data ?? event ?? msg;
	return {
		buildId: payload?.buildId ?? payload?.BuildID,
		status: payload?.status ?? payload?.Status,
	};
}

// SDK returns Variable.Type as a generic string; locally we expect a literal
// union driving the variable-viewer UI. Anything outside the known set is
// treated as a free-form user expression rather than discarded.
const KNOWN_TRACE_TYPES = new Set(["Variable", "FormalParameter", "UserExpression"]);
function narrowVariableTraceType(value: string): "Variable" | "FormalParameter" | "UserExpression" {
	return KNOWN_TRACE_TYPES.has(value)
		? (value as "Variable" | "FormalParameter" | "UserExpression")
		: "UserExpression";
}

export const buildKey: EventBusKey<BuildBusEvent> = Symbol("build-symbol-key");
export const BUILD_LIST_FORBIDDEN_MESSAGE = "Your account does not have access to these builds.";

/*
 * given n = builds returned per request, the SCDP will try to look for
 * active builds in the first (n * activeBuildMaxFetchDepth) builds in the database
 * at the time of writing n = 50
 */
const activeBuildMaxFetchDepth = 3;

export const useBuildsStore = defineStore("builds", {
	state: (): StoreState => ({
		buildHistory: undefined,
		activeBuilds: [],
		buildHistoryUpdateStatus: { loading: false, error: false, message: "" },
		activeBuildUpdateStatus: { loading: false, error: false, message: "" },
		buildListContinuationKey: undefined,
		activeBuildsListContinuationKey: undefined,
		buildEventBus: useEventBus(buildKey),
		realtimeSubscription: undefined,
		realtimeConnectInFlight: undefined,
		realtimeTeardownRequested: false,
		buildHistoryServedFromCache: false,
		buildIDsFetchedOverCache: markRaw(new Set<string>()),
		buildHistoryIsComplete: false,
		buildHistoryInitialisationInFlight: undefined,
	}),
	getters: {
		getBuildByProjectID: (state) => (projectID: string) => {
			if (!state.buildHistory) {
				return null;
			}
			return state.buildHistory.find((x) => x.BuildID === projectID) ?? null;
		},
		getBuildByID: (state) => (buildID: string) => {
			if (!state.buildHistory || !buildID) {
				return null;
			}
			return state.buildHistory.find((x) => x.BuildID === buildID) ?? null;
		},
		// Stale once a refresh failed, and still stale while the retry runs.
		buildHistoryIsStale: (state): boolean =>
			state.buildHistoryServedFromCache && state.buildHistoryUpdateStatus.error,
		inactiveBuilds(): Build[] {
			return (this.buildHistory ?? [])
				.filter((x) => !isBuildActive(x.Status))
				.sort(
					util.byNewestFirst(
						(build) => build.FinishedAt || build.CreatedAt,
						(build) => build.BuildID,
					),
				);
		},
		sortedActiveBuilds(): Build[] {
			// Optimistic builds arrive without CreatedAt
			return [...(this.activeBuilds ?? [])].sort(
				util.byNewestFirst(
					(build) => build.CreatedAt || Infinity,
					(build) => build.BuildID,
				),
			);
		},
	},
	actions: {
		async downloadBinary(
			buildID: string,
			coreClass: string,
			surface: BinaryDownloadSurface,
		): Promise<string | undefined> {
			let url: string;
			try {
				({ url } = await sdk.builds.getBinary(buildID));
			} catch (error) {
				if (
					error instanceof SdkError &&
					error.code === ERROR_CODES.API_NOT_FOUND &&
					error.message === kBinaryNotFoundSdkMessage
				) {
					return kBinaryMissingNotice;
				}
				throw error;
			}
			const response = await axios.get(url, { responseType: "blob" });
			util.triggerBlobDownload(response.data, `${buildID}.bin`);
			posthog.capture("build_binary_downloaded", { build_id: buildID, core_class: coreClass, surface });
			const sizeInBytes: number = response.data.size;
			if (coreClass === CoreClassE.C0MicroSD && sizeInBytes > kC0MicroSDApplicationLimitBytes) {
				return binaryTooLargeNotice(sizeInBytes);
			}
			return undefined;
		},
		async getBuildByIdOrFetch(buildID: string): Promise<Build> {
			const localBuild = this.getBuildByID(buildID);
			if (localBuild && (!this.buildHistoryServedFromCache || this.buildIDsFetchedOverCache.has(buildID))) {
				return localBuild;
			}
			const response = await sdk.builds.getOne(buildID);
			const buildFromRemote = response as Build;

			this.buildHistory = util.mergeByID(this.buildHistory ?? [], [buildFromRemote], (build) => build.BuildID);
			this.buildIDsFetchedOverCache.add(buildID);
			return buildFromRemote;
		},
		updateBuildByID(buildData: Build) {
			if (this.buildHistory) {
				this.buildHistory = util.mergeByID(this.buildHistory, [buildData], (build) => build.BuildID);
			}
			if (this.activeBuilds) {
				this.activeBuilds = util.mergeByID(this.activeBuilds, [buildData], (build) => build.BuildID);
			}
		},
		removeBuildByIdFromLocal(buildID: string) {
			if (this.buildHistory) {
				this.buildHistory = this.buildHistory.filter((x) => x.BuildID !== buildID);
			}
			this.removeFromActiveBuildList(buildID);
		},
		removeFromActiveBuildList(buildID: string) {
			if (this.activeBuilds) {
				this.activeBuilds = this.activeBuilds.filter((x) => x.BuildID !== buildID);
			}
		},
		addToActiveBuildList(buildData: Build) {
			if (!this.activeBuilds) {
				return;
			}
			// Dedup by BuildID so the optimistic add can't duplicate a WS-pushed build.
			this.activeBuilds = util.mergeByID(this.activeBuilds, [buildData], (build) => build.BuildID);
		},
		// Paint the cached history immediately. The fetch below replaces it once it lands.
		async hydrateBuildHistoryFromCache(historyAnswered: () => boolean = () => false) {
			const painted = await hydrateCachedList(
				BUILD_HISTORY_CACHE_KEY,
				isCachedBuild,
				() => this.buildHistory,
				(build) => build.BuildID,
			);
			if (painted && !historyAnswered()) {
				this.buildHistory = painted;
				this.buildHistoryServedFromCache = true;
				this.buildHistoryIsComplete = true;
				this.buildIDsFetchedOverCache.clear();
			}
		},
		async initialiseBuildHistory() {
			this.buildHistoryInitialisationInFlight ??= (async () => {
				localStorage.removeItem("build-list");
				mirrorStoreListToCache(
					this,
					BUILD_HISTORY_CACHE_KEY,
					() =>
						this.buildHistoryIsComplete && this.buildHistory
							? [...this.buildHistory].sort(
									util.byNewestFirst(
										(build) => build.CreatedAt,
										(build) => build.BuildID,
									),
								)
							: undefined,
					LIST_CACHE_MAX_ENTRIES,
				);
				// Reset keys: the first fresh batch replaces the cached list rather than appending.
				this.buildListContinuationKey = undefined;
				this.activeBuildsListContinuationKey = undefined;
				let historyAnswered = false;
				await Promise.all([
					this.hydrateBuildHistoryFromCache(() => historyAnswered),
					this.fetchNextBuildBatchFromRemote(() => {
						historyAnswered = true;
					}),
				]);
				await this.fetchActiveBuildsFromRemote();
			})().finally(() => {
				this.buildHistoryInitialisationInFlight = undefined;
			});
			return this.buildHistoryInitialisationInFlight;
		},

		// Refetch the active list from scratch to recover from missed
		// WS status pushes (sleeping tab, dropped messages, etc.).
		async reconcileActiveBuilds() {
			if (this.activeBuildUpdateStatus.loading) return;
			// IDs that drop out of the active list have terminated; resolve each so
			// the status bus and history converge even when the WS missed the push.
			const previouslyActiveIds = (this.activeBuilds ?? []).map((build) => build.BuildID);
			this.activeBuildsListContinuationKey = undefined;
			await this.fetchActiveBuildsFromRemote();
			const stillActiveIds = new Set((this.activeBuilds ?? []).map((build) => build.BuildID));
			const terminatedIds = previouslyActiveIds.filter((buildID) => !stillActiveIds.has(buildID));
			await Promise.all(
				terminatedIds.map(async (buildID) => {
					// Skip if the WS already converged it (avoid double-firing completion).
					const known = this.getBuildByID(buildID);
					if (known && isBuildTerminated(known.Status)) return;
					try {
						const finalBuild = await sdk.builds.getOne(buildID);
						this.handleBuildUpdates(finalBuild as Build);
					} catch (error) {
						Sentry.captureException(error);
					}
				}),
			);
		},
		async fetchActiveBuildsFromRemote() {
			try {
				this.activeBuildUpdateStatus.loading = true;
				let requestCounter = 0;
				do {
					await this.fetchNextActiveBuildBatchFromRemote();
					requestCounter += 1;
				} while (
					requestCounter < activeBuildMaxFetchDepth &&
					this.activeBuildsListContinuationKey !== undefined
				);
			} catch (error) {
				if (error instanceof SdkError && error.code === ERROR_CODES.API_NOT_FOUND) {
					this.activeBuilds = [];
				}
			} finally {
				this.activeBuildUpdateStatus.loading = false;
			}
		},
		async fetchNextActiveBuildBatchFromRemote() {
			try {
				const response = await this.fetchBuildsFromRemote(this.activeBuildsListContinuationKey, [
					"Accepted",
					"Initialising",
					"In Progress",
				]);

				// Always assign (even on empty) so a finished last build can't linger.
				const fetchedActiveBuilds = (response.Builds as Build[]) ?? [];
				this.activeBuilds =
					this.activeBuilds && this.activeBuildsListContinuationKey
						? util.mergeByID(this.activeBuilds, fetchedActiveBuilds, (build) => build.BuildID)
						: fetchedActiveBuilds;
				this.activeBuildsListContinuationKey = response.ContinuationKey;
				this.activeBuildUpdateStatus.message = "";
			} catch (error) {
				if (error instanceof SdkError && error.code === ERROR_CODES.API_NOT_FOUND) {
					// No builds found. set the history to be empty
					this.activeBuilds = [];
				}
				util.recordListFetchError(
					error,
					this.activeBuildUpdateStatus,
					BUILD_LIST_FORBIDDEN_MESSAGE,
					"Get active builds from remote",
				);
			}
		},

		async fetchNextBuildBatchFromRemote(onAnswered?: () => void) {
			try {
				this.buildHistoryUpdateStatus.loading = true;
				const response = await this.fetchBuildsFromRemote(this.buildListContinuationKey);
				// The refresh answered, whatever shape it came back in.
				onAnswered?.();
				this.buildHistoryServedFromCache = false;
				const fetchedBuilds = (response.Builds as Build[] | undefined) ?? [];
				this.buildHistory =
					this.buildHistory && this.buildListContinuationKey
						? util.mergeByID(this.buildHistory, fetchedBuilds, (build) => build.BuildID)
						: fetchedBuilds;
				this.buildHistoryIsComplete = true;
				this.buildListContinuationKey = response.ContinuationKey;
				this.buildHistoryUpdateStatus.message = "";
			} catch (error) {
				const answered =
					error instanceof SdkError &&
					(error.code === ERROR_CODES.API_FORBIDDEN || error.code === ERROR_CODES.API_NOT_FOUND);
				if (answered) {
					onAnswered?.();
				}
				if (
					error instanceof SdkError &&
					error.code === ERROR_CODES.API_FORBIDDEN &&
					this.buildHistoryServedFromCache
				) {
					this.buildHistory = undefined;
					this.buildHistoryServedFromCache = false;
					this.buildHistoryIsComplete = false;
				}
				if (error instanceof SdkError && error.code === ERROR_CODES.API_NOT_FOUND) {
					this.buildHistory = [];
					this.buildHistoryServedFromCache = false;
					this.buildHistoryIsComplete = true;
				}
				util.recordListFetchError(
					error,
					this.buildHistoryUpdateStatus,
					BUILD_LIST_FORBIDDEN_MESSAGE,
					"Get builds from remote",
				);
			} finally {
				this.buildHistoryUpdateStatus.loading = false;
			}
		},

		async fetchBuildsFromRemote(startKey?: string, buildStatus?: BuildStatus[]) {
			const authUserStore = useUserStore();
			await util.waitFor(() => authUserStore.currentUserSessionObject !== undefined);
			return sdk.builds.list({
				startKey,
				status: buildStatus as SdkBuildStatus[] | undefined,
			});
		},
		async fetchAndParseVariables(buildID: string) {
			let discoveredVariables: TraceVariableWithTraceType[] = [];
			try {
				const response = await sdk.builds.getVariables(buildID);
				if (response.Variables) {
					discoveredVariables = response.Variables.map((variableTrace): TraceVariableWithTraceType => ({
						Expression: variableTrace.Name,
						File: variableTrace.File,
						LineNumber: variableTrace.Line,
						Type: narrowVariableTraceType(variableTrace.Type),
					}));
				}
			} catch (error) {
				monitoringCaptureError(error, "Parse task variables");
			}
			return discoveredVariables;
		},
		/*
		 *	Subscribe to the user's realtime build-status channel. The push
		 *	carries buildId + status. On non-terminal transitions we merge the
		 *	status delta into the cached record and skip the REST refetch; on
		 *	terminal status (or first sighting with no cached record) we
		 *	refetch to pick up fields the WS payload doesn't carry (output
		 *	URLs, final stats). Idempotent — a second call while a
		 *	subscription is live is a no-op.
		 */
		initRealtime(userId: string): Promise<boolean> {
			if (this.realtimeSubscription) return Promise.resolve(true);
			if (!this.realtimeConnectInFlight) {
				this.realtimeConnectInFlight = this.connectRealtime(userId).finally(() => {
					this.realtimeConnectInFlight = undefined;
				});
			}
			return this.realtimeConnectInFlight;
		},
		async connectRealtime(userId: string): Promise<boolean> {
			this.realtimeTeardownRequested = false;
			let subscription: RealtimeSubscription;
			try {
				await sdk.realtime.connect();
				subscription = await sdk.realtime.channel(`build-status/${idToChannelName(userId)}`).subscribe();
			} catch (err) {
				console.error("Failed to initialize realtime build channel:", err);
				if (sdk.realtime.getState() === "open") useRealtimeStore().reportChannelFailure(err);
				return false;
			}
			// Honor a teardown that raced this connect; otherwise the subscription leaks.
			if (this.realtimeTeardownRequested) {
				subscription.close();
				return false;
			}
			this.realtimeSubscription = subscription;

			subscription.on("message", async (msg: BuildStatusMessage) => {
				const { buildId, status } = extractBuildStatusUpdate(msg);
				if (!buildId) return;

				const cached = this.getBuildByID(buildId);
				// Ignore pushes for an already-terminal build so we don't re-fire completion.
				if (cached && isBuildTerminated(cached.Status)) {
					return;
				}
				const isTerminal = !!status && isBuildTerminated(status as BuildStatus);
				if (cached && status && !isTerminal) {
					this.handleBuildUpdates({ ...cached, Status: status as BuildStatus });
					return;
				}

				try {
					const response = await sdk.builds.getOne(buildId);
					this.handleBuildUpdates(response as Build);
				} catch (err) {
					Sentry.captureException(err);
				}
			});
			subscription.on("error", (err: unknown) => {
				console.error("Realtime build channel error:", err);
				if (isRealtimeGiveUpError(err)) return;
				subscription.close();
				this.realtimeSubscription = undefined;
				useRealtimeStore().reportChannelFailure(err);
			});

			// No snapshot on subscribe: fetch once on connect so an already-running build is not missed.
			await this.fetchActiveBuildsFromRemote();
			return true;
		},
		teardownRealtime() {
			// Mark teardown so an in-flight initRealtime() closes its not-yet-assigned subscription.
			this.realtimeTeardownRequested = true;
			if (this.realtimeSubscription) {
				this.realtimeSubscription.close();
				this.realtimeSubscription = undefined;
			}
		},
		handleBuildUpdates(buildData: Build) {
			// update the build object in build history
			this.updateBuildByID(buildData);

			// Check if build is terminated/completed
			if (isBuildTerminated(buildData.Status)) {
				this.removeFromActiveBuildList(buildData.BuildID);

				this.buildEventBus.emit({ type: BuildEventE.BuildCompleted, buildData: buildData });

				const rootStore = useRootStore();
				rootStore.tierLimitEventBus.emit({
					type: TierLimitEventTypeE.UsageChanged,
					affectedLimits: [
						UserLimitsE.RegistryStorageBytesMaximum,
						UserLimitsE.BuildTimeInMillisecondsCount,
						UserLimitsE.ConcurrentBuildCount,
					],
				});
				return;
			}
			// Update status of ongoing builds
			this.buildEventBus.emit({ type: BuildEventE.BuildUpdated, buildData: buildData });
		},
	},
});
